import { memo, useState, useRef, useEffect } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import PropTypes from 'prop-types';

import * as Styled from './ThreadListItem.style';
import { useUpdateThreadTitle } from '../../hooks/useUpdateThreadTitle';

/**
 * Individual thread item component for the sidebar
 * @param {object} props - Component props
 * @param {object} props.thread - Thread object containing thread data
 * @param {boolean} [props.isActive=false] - Whether this thread is currently active
 * @param {Function} props.onSelect - Callback when thread is selected
 * @param {Function} [props.onError] - Callback when an error occurs
 * @returns {React.ReactElement} The ThreadListItem component
 */
function ThreadListItem({ thread, isActive = false, onSelect, onError }) {
  const { threadUuid, title, createdAt } = thread;
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [currentTitle, setCurrentTitle] = useState(title);
  const inputRef = useRef(null);
  const { updateThreadTitle, loading } = useUpdateThreadTitle();

  // Format display title with fallback to formatted date and time
  const displayTitle =
    currentTitle ||
    `${new Date(createdAt).toLocaleDateString([], {
      month: 'short',
      day: 'numeric'
    })}, ${new Date(createdAt).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit'
    })}`;

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (!isEditing) {
      onSelect(threadUuid);
    }
  };

  const handleKeyDown = (event) => {
    if (!isEditing && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onSelect(threadUuid);
    }
  };

  const handleEditClick = (event) => {
    event.stopPropagation();
    setEditedTitle(currentTitle || '');
    setIsEditing(true);
  };

  const handleSaveTitle = async () => {
    // Trim excess whitespace from the title
    const trimmedTitle = editedTitle.trim();

    // Don't call API if title is empty or unchanged (after trimming)
    if (!trimmedTitle || trimmedTitle === currentTitle) {
      setIsEditing(false);
      return;
    }

    // Validate max length (100 characters as per API docs)
    if (trimmedTitle.length > 100) {
      setIsEditing(false);
      if (onError) {
        onError('Title must be 100 characters or less');
      }
      return;
    }

    try {
      await updateThreadTitle(threadUuid, trimmedTitle);
      setCurrentTitle(trimmedTitle);
      setIsEditing(false);
    } catch (err) {
      // On error, keep the original title and exit edit mode
      setIsEditing(false);

      // Notify parent of error for toast display
      if (onError) {
        onError(err.message || 'Failed to update thread title');
      }
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedTitle('');
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSaveTitle();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelEdit();
    }
  };

  const handleInputBlur = () => {
    handleCancelEdit();
  };

  const handleInputClick = (event) => {
    event.stopPropagation();
  };

  return (
    <Styled.ThreadItemContainer
      aria-label={`Switch to thread: ${displayTitle}`}
      data-testid={`thread-item-${threadUuid}`}
      isActive={isActive}
      role="button"
      tabIndex={isEditing ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Styled.ThreadContent>
        <Styled.ThreadHeader>
          {isEditing ? (
            <Styled.TitleInput
              ref={inputRef}
              disabled={loading}
              type="text"
              value={editedTitle}
              onBlur={handleInputBlur}
              onChange={(e) => setEditedTitle(e.target.value)}
              onClick={handleInputClick}
              onKeyDown={handleInputKeyDown}
            />
          ) : (
            <>
              <Styled.ThreadTitle isActive={isActive} size="sm">
                {displayTitle}
              </Styled.ThreadTitle>
              <Styled.EditButton
                aria-label="Edit thread title"
                data-edit-button
                role="button"
                tabIndex={0}
                onClick={handleEditClick}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleEditClick(e);
                  }
                }}
              >
                <EditIcon />
              </Styled.EditButton>
            </>
          )}
        </Styled.ThreadHeader>
      </Styled.ThreadContent>
    </Styled.ThreadItemContainer>
  );
}

ThreadListItem.propTypes = {
  thread: PropTypes.shape({
    threadUuid: PropTypes.string.isRequired,
    title: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    messageCount: PropTypes.number
  }).isRequired,
  isActive: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onError: PropTypes.func
};

// Memoize to prevent unnecessary re-renders when parent updates
export default memo(ThreadListItem);
